import { css } from 'lit';
import { hostReset, floatingPanel, placementOrigins } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

export default [
  hostReset,
  floatingPanel('.panel', { durationVar: '--uwc-ac-duration', durationDefault: '140ms' }),
  placementOrigins,
  css`
    :host { display: inline-block; }
    :host([fluid]) { width: 100%; display: block; }

    /* ── Root input wrapper (trigger + float label) ─────────────────────────── */
    .ac-root {
      --_bg:        var(--uwc-ac-bg,        ${t.surface});
      --_border:    var(--uwc-ac-border,    1px solid ${t.borderControl});
      --_radius:    var(--uwc-ac-radius,    ${t.radiusMd});
      --_color:     var(--uwc-ac-color,     ${t.text});
      --_ph-color:  ${t.textMuted};

      position:      relative;    /* float-label positioning context */
      display:       inline-flex;
      align-items:   center;
      flex-wrap:     wrap;
      gap:           ${t.space1};
      width:         100%;
      min-height:    ${t.controlHeightMd};
      padding:       ${t.space2} ${t.space3};
      font-family:   var(--uwc-font-family, inherit);
      font-size:     var(--uwc-ac-font-size, ${t.fontSizeMd});
      background:    var(--_bg);
      border:        var(--_border);
      border-radius: var(--_radius);
      color:         var(--_color);
      box-sizing:    border-box;
      transition:    border-color ${t.durationBase}, box-shadow ${t.durationBase};
      cursor:        text;
    }

    :host(:not([fluid])) .ac-root { min-width: 160px; }

    /* Focus ring */
    .ac-root--focused,
    .ac-root:focus-within {
      border-color: var(--uwc-ac-focus-border, var(--uwc-color-primary, ${t.primary}));
      box-shadow:   0 0 0 3px
        color-mix(in oklab, var(--uwc-color-primary, ${t.primary}) 20%, transparent);
    }

    /* Open (panel visible) */
    .ac-root--open {
      border-color: var(--uwc-ac-focus-border, var(--uwc-color-primary, ${t.primary}));
      box-shadow:   0 0 0 3px
        color-mix(in oklab, var(--uwc-color-primary, ${t.primary}) 20%, transparent);
    }

    /* Disabled */
    :host([disabled]) { opacity: 0.55; }
    .ac-root--disabled {
      background:     var(--uwc-ac-disabled-bg, ${t.surfaceRaised});
      color:          ${t.textDisabled};
      cursor:         not-allowed;
      pointer-events: none;
    }

    /* Invalid */
    .ac-root--invalid {
      border-color: var(--uwc-color-danger, ${t.danger});
    }
    .ac-root--invalid.ac-root--focused,
    .ac-root--invalid.ac-root--open,
    .ac-root--invalid:focus-within {
      border-color: var(--uwc-color-danger, ${t.danger});
      box-shadow:   0 0 0 3px
        color-mix(in oklab, var(--uwc-color-danger, ${t.danger}) 20%, transparent);
    }

    /* Filled variant */
    .ac-root--filled {
      --_bg: var(--uwc-ac-filled-bg, ${t.surfaceRaised});
      border-color: transparent;
    }
    .ac-root--filled.ac-root--focused,
    .ac-root--filled.ac-root--open,
    .ac-root--filled:focus-within {
      --_bg: ${t.surface};
      border-color: var(--uwc-color-primary, ${t.primary});
    }

    /* ── Size variants ──────────────────────────────────────────────────────── */
    :host([size="small"]) .ac-root {
      min-height:  ${t.controlHeightSm};
      padding:     0.1875rem 0.5rem;
      font-size:   ${t.fontSizeXs};
    }
    :host([size="large"]) .ac-root {
      min-height:  ${t.controlHeightLg};
      padding:     ${t.space2} ${t.space4};
      font-size:   ${t.fontSizeMd};
    }

    /* Lock to 33px single-row layout in multiple mode — mirrors uwc-dropdown chip trigger */
    :host([multiple]) .ac-root {
      height:    ${t.controlHeightMd};
      min-height: unset;
      flex-wrap:  nowrap;
      overflow:   hidden;
      padding:    ${t.space1};
    }

    /* ── Native input ───────────────────────────────────────────────────────── */
    .ac-input {
      flex:        1;
      min-width:   4rem;
      border:      none;
      outline:     none;
      background:  transparent;
      font-family: inherit;
      font-size:   inherit;
      color:       inherit;
      padding:     0;
      line-height: 1;
      cursor:      text;
    }
    .ac-input::placeholder { color: var(--_ph-color); }
    .ac-input:disabled      { cursor: not-allowed; }

    /* ── Prefix / Suffix ────────────────────────────────────────────────────── */
    .ac-prefix,
    .ac-suffix {
      display:     flex;
      align-items: center;
      flex-shrink: 0;
      color:       ${t.textMuted};
    }
    .ac-prefix { order: -1; margin-right: ${t.space1}; }
    .ac-suffix { margin-left:  ${t.space1}; }

    /* ── Chips (multiple mode) ──────────────────────────────────────────────── */
    .chips-wrapper {
      display:  flex;
      flex-wrap: wrap;
      gap:       ${t.space1};
      flex:      none;
    }
    .chip {
      display:       inline-flex;
      align-items:   center;
      gap:           ${t.space1};
      padding:       3px 0 3px ${t.space2};
      background:    var(--uwc-ac-chip-bg,    ${t.borderSubtle});
      color:         var(--uwc-ac-chip-color, ${t.textSecondary});
      border-radius: ${t.radiusSm};
      font-size:     ${t.fontSizeMd};
      white-space:   nowrap;
      line-height:   1;
    }
    .chip--overflow {
      padding:    ${t.space1} ${t.space2};
      background: ${t.borderSubtle};
      color:      ${t.textSecondary};
    }
    .chip-remove {
      background:  none;
      border:      none;
      cursor:      pointer;
      color:       ${t.textMuted};
      transition:  color ${t.durationFast};
    }
    .chip-remove:hover { color: ${t.text}; }

    /* ── Actions area (clear / loading / dropdown btn) ──────────────────────── */
    .ac-actions {
      display:     flex;
      align-items: center;
      gap:         ${t.space1};
      flex-shrink: 0;
      margin-left: auto;
    }

    /* Clear button */
    .clear-btn {
      display:         flex;
      align-items:     center;
      justify-content: center;
      width:           18px;
      height:          18px;
      color:           ${t.textMuted};
      cursor:          pointer;
      border-radius:   ${t.radiusFull};
      transition:      background ${t.durationFast}, color ${t.durationFast};
    }
    .clear-btn:hover { background: ${t.hoverBg}; color: ${t.text}; }

    /* Loading spinner */
    .loading-icon {
      display:       block;
      width:         14px;
      height:        14px;
      border:        2px solid ${t.borderControl};
      border-top-color: var(--uwc-color-primary, ${t.primary});
      border-radius: ${t.radiusFull};
      animation:     ac-spin 600ms linear infinite;
      flex-shrink:   0;
    }
    @keyframes ac-spin { to { transform: rotate(360deg); } }

    /* Dropdown button */
    .dropdown-btn {
      display:         flex;
      align-items:     center;
      justify-content: center;
      width:           22px;
      height:          15px;
      border:          none;
      background:      transparent;
      color:           ${t.textMuted};
      cursor:          pointer;
      border-radius:   ${t.radiusSm};
      padding:         0;
      transition:      background ${t.durationFast}, color ${t.durationFast};
    }
    .dropdown-btn:hover { background: ${t.hoverBg}; color: ${t.text}; }
    .dropdown-btn:disabled { opacity: 0.4; cursor: not-allowed; }

    /* ── Float label ────────────────────────────────────────────────────────── */

    /* Base — starts centred inside the ac-root (acts as placeholder) */
    .float-label {
      position:       absolute;
      left:           calc(${t.space3} + 1px);
      top:            50%;
      transform:      translateY(-50%);
      font-size:      ${t.fontSizeSm};
      color:          ${t.textMuted};
      pointer-events: none;
      line-height:    1;
      padding-inline: 0.125rem;
      white-space:    nowrap;
      transition:
        top       0.18s ease,
        transform 0.18s ease,
        font-size 0.18s ease,
        color     0.18s ease;
    }

    /* ── "over": animates from inside → above the root border ─────────────── */
    :host([float-label="over"]) .ac-root {
      margin-top: 1.5rem;
      overflow:   visible;
    }
    :host([float-label="over"]) .float-label.float-label--active {
      top:       0;
      transform: translateY(calc(-100% - 0.25rem));
      font-size: ${t.fontSizeXs};
      color:     var(--uwc-color-primary, ${t.primary});
    }
    :host([float-label="over"]) .ac-root:not(.ac-root--multiple) .ac-input::placeholder { color: transparent; }
    /* When there's no value and panel is closed, placeholder hides (label is inside) */
    :host([float-label="over"]) .ac-input:not(:focus)::placeholder { color: transparent; }
    :host([float-label="over"]) .float-label.float-label--active ~ .ac-input::placeholder,
    :host([float-label="over"]) .ac-root--focused .ac-input::placeholder { color: var(--_ph-color); }

    /* ── "in": floats to INSIDE TOP — never crosses the border ────────────── */
    :host([float-label="in"]) .ac-root {
      padding-top:    1.125rem;
      min-height:     2.625rem;
      align-items:    flex-end;
      padding-bottom: 0.375rem;
    }
    :host([float-label="in"]) .float-label.float-label--active {
      top:       0.3125rem;
      transform: none;
      font-size: ${t.fontSizeXs};
      color:     var(--uwc-color-primary, ${t.primary});
    }
    :host([float-label="in"]) .ac-input::placeholder { color: transparent; }
    :host([float-label="in"]) .ac-root--focused .ac-input::placeholder { color: var(--_ph-color); }

    /* ── "on": label sits ON (straddles) the upper border ─────────────────── */
    :host([float-label="on"]) .ac-root {
      margin-top: 0.75rem;
      overflow:   visible;
    }
    :host([float-label="on"]) .float-label {
      background: var(--_bg);
      padding:    0 4px;
    }
    :host([float-label="on"]) .float-label.float-label--active {
      top:       0;
      transform: translateY(-50%);
      font-size: ${t.fontSizeXs};
      color:     var(--uwc-color-primary, ${t.primary});
    }
    :host([float-label="on"]) .ac-input::placeholder { color: transparent; }
    :host([float-label="on"]) .ac-root--focused .ac-input::placeholder { color: var(--_ph-color); }

    /* ── Floating panel ─────────────────────────────────────────────────────── */
    .panel {
      background:    var(--uwc-ac-panel-bg,     ${t.surface});
      border:        var(--uwc-ac-panel-border, 1px solid ${t.border});
      border-radius: var(--uwc-ac-panel-radius, ${t.radiusMd});
      box-shadow:    var(--uwc-ac-panel-shadow,  ${t.shadowLg});
      z-index:       var(--uwc-ac-z,             ${t.zFloat});
      min-width:     160px; /* overridden at runtime by afterPosition → trigger width */
      overflow:      hidden;
    }

    /* ── Panel header / footer ──────────────────────────────────────────────── */
    .panel-header {
      padding:       ${t.space2} ${t.space3};
      border-bottom: 1px solid ${t.borderSubtle};
      flex-shrink:   0;
    }
    .panel-footer {
      padding:    ${t.space2} ${t.space3};
      border-top: 1px solid ${t.borderSubtle};
      flex-shrink: 0;
      font-size:  ${t.fontSizeXs};
      color:      ${t.textMuted};
    }

    /* ── Options wrapper (non-virtual scroll container) ─────────────────────── */
    .options-wrapper {
      overflow-y: auto;
      scrollbar-width: thin;
      scrollbar-color: ${t.borderControl} transparent;
    }
    .options-wrapper::-webkit-scrollbar       { width: 6px; }
    .options-wrapper::-webkit-scrollbar-track { background: transparent; }
    .options-wrapper::-webkit-scrollbar-thumb {
      background:    ${t.borderControl};
      border-radius: ${t.radiusFull};
    }

    /* ── Options list ───────────────────────────────────────────────────────── */
    .options-list { list-style: none; margin: 0; padding: 0; }

    /* ── Virtual scroll container ───────────────────────────────────────────── */
    .options-scroll {
      overflow-anchor: none;
      scrollbar-width: thin;
      scrollbar-color: ${t.borderControl} transparent;
    }
    .options-scroll::-webkit-scrollbar       { width: 6px; }
    .options-scroll::-webkit-scrollbar-track { background: transparent; }
    .options-scroll::-webkit-scrollbar-thumb {
      background:    ${t.borderControl};
      border-radius: ${t.radiusFull};
    }
    .options-scroll * { overflow-anchor: none; }

    /* ── Group header ───────────────────────────────────────────────────────── */
    .group-header {
      display:        flex;
      align-items:    center;
      gap:            7px;
      padding:        ${t.space2} 13px 4px;
      font-size:      ${t.fontSizeXs};
      font-weight:    ${t.fontWeightBold};
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color:          var(--uwc-ac-group-color, ${t.textMuted});
      cursor:         default;
    }

    /* ── Option ─────────────────────────────────────────────────────────────── */
    .option {
      display:     flex;
      align-items: center;
      gap:         9px;
      padding:     0 13px;
      height:      ${t.controlHeightMd};
      box-sizing:  border-box;
      font-size:   ${t.fontSizeMd};
      color:       ${t.text};
      cursor:      pointer;
      list-style:  none;
      overflow:    hidden;
      transition:  background ${t.durationFast};
    }
    .option:hover,
    .option--highlighted         { background: var(--uwc-ac-option-hover-bg, ${t.hoverBg}); }
    .option--selected {
      background:  var(--uwc-ac-option-selected-bg,    ${t.selectedBg});
      color:       var(--uwc-ac-option-selected-color, var(--uwc-color-primary, ${t.primary}));
      font-weight: ${t.fontWeightMedium};
    }
    .option--selected.option--highlighted {
      background: color-mix(in oklab, var(--uwc-color-primary, ${t.primary}) 15%, transparent);
    }
    .option--disabled            { color: ${t.textDisabled}; cursor: not-allowed; }
    .option--disabled:hover      { background: transparent; }

    .opt-icon  { width: 18px; text-align: center; flex-shrink: 0; }
    .opt-label { flex: 1; }
    .opt-badge {
      font-size:     ${t.fontSizeXs};
      padding:       1px 6px;
      border-radius: ${t.radiusFull};
      background:    ${t.borderSubtle};
      color:         ${t.textSecondary};
    }
    .opt-check {
      display:         flex;
      align-items:     center;
      justify-content: center;
      color:           var(--uwc-color-primary, ${t.primary});
      margin-left:     auto;
      flex-shrink:     0;
    }

    /* ── Empty state ────────────────────────────────────────────────────────── */
    .empty {
      padding:    20px ${t.space4};
      text-align: center;
      font-size:  ${t.fontSizeXs};
      color:      ${t.textDisabled};
    }

    /* ── Loading skeleton ───────────────────────────────────────────────────── */
    .skeleton-item {
      display:     flex;
      align-items: center;
      padding:     ${t.space2} 13px;
      border-bottom: 1px solid ${t.borderSubtle};
    }
    .skeleton-item:last-child { border-bottom: none; }
    .skeleton-bar {
      height:        0.75rem;
      border-radius: ${t.radiusSm};
      background:    linear-gradient(
        90deg,
        ${t.borderSubtle} 25%,
        ${t.border}       50%,
        ${t.borderSubtle} 75%
      );
      background-size: 200% 100%;
      animation:       ac-shimmer 1.4s ease infinite;
    }
    @keyframes ac-shimmer {
      0%   { background-position:  200% 0; }
      100% { background-position: -200% 0; }
    }

    @media (prefers-reduced-motion: reduce) {
      .skeleton-bar,
      .loading-icon { animation: none; }
    }
  `,
];
